import React, { useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faFutbol, faHockeyPuck } from "@fortawesome/pro-regular-svg-icons";
import CardIcon from "./CardIcon";
import { CardTypeEnum, EventType, EventTypeEnum, GoalTypeEnum, SportEnum } from "../../../../client/api";
import { ClavaContext } from "../../../../config/contexts";
import { IDType } from "../../../../config/types";
import { showTranslated, translate } from "../../../../config/translator";
import { pathPlayerView } from "../../../../config/routes";
import PlayerImage from "../../PlayerImage";
import StoreMiniBanner from "../../StoreMiniBanner";
import ScaleFade from "../../Animated/ScaleFade";


type Align = "start" | "end";

type MatchEventSideProps = { event: EventType; align: Align; scorePrefix?: string };

const MatchEventSide: React.FC<MatchEventSideProps> = ({ event, align, scorePrefix }) => {
  const { l } = useContext(ClavaContext);
  const subtitleAlign = align === "end" ? "text-right" : "text-left";
  const nameAlign = align === "end" ? "text-right" : "text-left";
  const scorePrefixDisplay = scorePrefix ? scorePrefix.replace(" - ", " : ") : "";
  const formatName = (familyName: string, givenName: string, maxLength = 34) => {
    const full = `${familyName} ${givenName}`;
    if (full.length <= maxLength) return full;
    const remaining = Math.max(1, maxLength - familyName.length - 2);
    const abbrevLen = Math.max(1, Math.min(3, remaining));
    return `${familyName} ${givenName.slice(0, abbrevLen)}.`;
  };

  const baseName = event.player
    ? formatName(event.player.familyName, event.player.givenName)
    : translate(event.type === EventTypeEnum.GOAL ? "goal" : event.type === EventTypeEnum.CARD ? "card" : "chance", l);
  const subtitleParts: string[] = [];
  if (event.type === EventTypeEnum.GOAL) {
    if (scorePrefixDisplay) subtitleParts.push(scorePrefixDisplay);
    if (event.assist) subtitleParts.push(formatName(event.assist.familyName, event.assist.givenName, 30));
    if (!event.assist && event.goalType) subtitleParts.push(showTranslated(event.goalType.translation, l));
  } else if (event.type === EventTypeEnum.CHANCE && event.chanceType) {
    subtitleParts.push(showTranslated(event.chanceType.translation, l));
  }

  return (
    <div className="inline-flex flex-col justify-center h-10 w-56">
      <span
        className={`${nameAlign} font-medium text-font dark:text-font-dark text-sm whitespace-nowrap overflow-hidden text-ellipsis`}>
        {baseName}
      </span>
      {subtitleParts.length > 0 && (
        <span
          className={`font-normal ${subtitleAlign} text-font-600 dark:text-font-600-dark text-xs whitespace-nowrap overflow-hidden text-ellipsis`}>
          {subtitleParts.join(" - ")}
        </span>
      )}
    </div>
  );
};

type IconWithMinuteProps = {
  event: EventType;
  sports: SportEnum;
};

const IconWithMinute: React.FC<IconWithMinuteProps> = ({ event, sports }) => (
  <div className="relative w-12 h-12 flex-none">
    <div className="absolute inset-0 flex items-center justify-center">
      {event.type === EventTypeEnum.GOAL ? (
        sports === SportEnum.SOCCER && event.goalType && event.goalType.key === GoalTypeEnum.OWN_GOAL ? (
          <i className="fa-kit fa-regular-futbol-circle-arrow-left text-red text-base" />
        ) : (
          <FontAwesomeIcon
            icon={sports === SportEnum.SOCCER ? faFutbol : faHockeyPuck}
            className={`w-7 h-7 ${
              event.goalType && event.goalType.key === GoalTypeEnum.OWN_GOAL && sports !== SportEnum.SOCCER
                ? "text-red"
                : "text-bg-900 dark:text-bg-900-dark"
            }`}
          />
        )
      ) : event.type === EventTypeEnum.CARD ? (
        <CardIcon type={event.cardType?.key ?? CardTypeEnum.YELLOW} width={20} />
      ) : event.type === EventTypeEnum.CHANCE ? (
        <FontAwesomeIcon icon={faBolt} className="w-7 h-7 text-font-600 dark:text-font-600-dark" />
      ) : null}
    </div>
    <span
      className="absolute -bottom-3 left-1/2 -translate-x-1/2 inline-block px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-50-dark text-primary text-xs font-semibold">
      {event.minute != null ? `${event.minute}'` : "-"}
    </span>
  </div>
);

type MatchEventProps = {
  event: EventType;
  team1Id: IDType;
  sports: SportEnum;
  allEvents: EventType[];
  currentEventIndex: number;
};

const MatchEvent: React.FC<MatchEventProps> = ({ event, sports, team1Id, allEvents, currentEventIndex }) => {
  const { l } = useContext(ClavaContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const onAvatarClick = useCallback(() => {
    if (event.player?.id) {
      navigate(pathPlayerView(event.player.id));
    } else {
      setOpen(true);
    }
  }, [event.player?.id, navigate]);
  const onBannerClose = useCallback(() => setOpen(false), []);
  const isTeam1 = String(event.teamId) === String(team1Id);

  const calculateScore = useCallback(() => {
    let team1Score = 0;
    let team2Score = 0;

    for (let i = 0; i <= currentEventIndex; i++) {
      const currentEvent = allEvents[i];
      if (currentEvent.type === EventTypeEnum.GOAL) {
        const isCurrentEventTeam1 = String(currentEvent.teamId) === String(team1Id);
        if (isCurrentEventTeam1) {
          team1Score++;
        } else {
          team2Score++;
        }
      }
    }

    return { team1Score, team2Score };
  }, [allEvents, currentEventIndex, team1Id]);

  let scorePrefix: string | undefined;
  if (event.type === EventTypeEnum.GOAL) {
    const { team1Score, team2Score } = calculateScore();
    scorePrefix = `${team1Score} - ${team2Score}`;
  }
  return (
    <div className="flex flex-row items-start my-4 space-x-16 w-full">
      <div className="flex-1 flex flex-row items-start justify-end pr-0">
        {isTeam1 && (
          <div className="flex flex-row items-center gap-5">
            <div className="w-12 flex-none">
              <IconWithMinute event={event} sports={sports} />
            </div>
            <div className="w-px h-12 bg-light-gray dark:bg-light-gray-dark flex-none" />
            <div className="relative w-12 h-12 flex-none">
              <ScaleFade isOpen hoverOnContainer className="w-12 h-12">
                <button type="button" onClick={onAvatarClick}
                        className="w-12 h-12 rounded-full overflow-hidden cursor-pointer">
                  <PlayerImage image={event.player?.photo ?? undefined} width={48} className="rounded-full" />
                </button>
              </ScaleFade>
              {open && (
                <div className="absolute -top-2 left-8 z-10">
                  <StoreMiniBanner compact noBorder message={translate("downloadUnlimited", l)} onClose={onBannerClose}
                                   autoHideMs={6000} />
                </div>
              )}
            </div>
            <MatchEventSide event={event} align="start" scorePrefix={scorePrefix} />
          </div>
        )}
      </div>
      <div className="flex-1 flex flex-row items-start justify-start pl-0">
        {!isTeam1 && (
          <div className="flex flex-row items-center gap-5">
            <MatchEventSide event={event} align="end" scorePrefix={scorePrefix} />
            <div className="relative w-12 h-12 flex-none">
              <ScaleFade isOpen hoverOnContainer className="w-12 h-12">
                <button type="button" onClick={onAvatarClick}
                        className="w-12 h-12 rounded-full overflow-hidden cursor-pointer">
                  <PlayerImage image={event.player?.photo ?? undefined} width={48} className="rounded-full" />
                </button>
              </ScaleFade>
              {open && (
                <div className="absolute -top-2 right-8 z-10">
                  <StoreMiniBanner compact noBorder message={translate("downloadUnlimited", l)} onClose={onBannerClose}
                                   autoHideMs={6000} />
                </div>
              )}
            </div>
            <div className="w-px h-12 bg-light-gray dark:bg-light-gray-dark flex-none" />
            <div className="w-12 flex-none">
              <IconWithMinute event={event} sports={sports} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchEvent;
